import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSnapshot } from 'valtio'
import { useTranslation } from 'react-i18next'

import { ScreenHeader } from '@app/components'
import ListWithSearch from '@app/components/ListWithSearch'
import { Divider, IconTypes, Modal, Button } from '@app/ui'
import { ModalType, SelectIconType, UserAuthStateType, ButtonSize, ButtonType } from '@app/types'
import { UserAuthState } from '@app/storage'
import { SelectListHelper } from '@app/helpers'
import i18n from '@app/libraries/i18n'
import { themeManager } from '@app/helpers/theme'

interface LoginChooseLanguageScreenProps {
  onBack: () => void;
}

export const LANG_ENUM = {
  ES: 'es',
  FR: 'fr',
  EN: 'en',
  SR: 'sr',
}

const LoginChooseLanguageScreen: FC<LoginChooseLanguageScreenProps> = ({
  onBack,
}) => {
  const { languagesList, userLanguageId, translatedCountry } = useSnapshot<UserAuthStateType>(UserAuthState)
  const [ searchString, setSearchString ] = useState('')
  const [ tempSelectedLanguageId, setTempSelectedLanguageId ] = useState(userLanguageId)
  const { t } = useTranslation()
  console.log({languagesList})

  const onSelectLanguage = (id: string, selected: boolean) => {
    if (selected) {
      setTempSelectedLanguageId(id)
    }
  }

  const handleConfirm = () => {
    localStorage.setItem('app_language', tempSelectedLanguageId)
    UserAuthState.userLanguageId = tempSelectedLanguageId
    i18n.changeLanguage(tempSelectedLanguageId)
    onBack()
  }

  const handleCancel = () => {
    onBack()
  }

  const onSearchLanguage = (search: string) => setSearchString(search)

  const languageElements = useMemo(() => {
    let selectedLanguageList = languagesList

    if (tempSelectedLanguageId === LANG_ENUM.FR || tempSelectedLanguageId === LANG_ENUM.ES || tempSelectedLanguageId === LANG_ENUM.SR) {
      selectedLanguageList = translatedCountry[tempSelectedLanguageId]
    }

    return SelectListHelper.transformToSelectList(selectedLanguageList, new Set([ tempSelectedLanguageId ]), { isFlag: true })
  }, [ languagesList, tempSelectedLanguageId ])


  return(
    <StyledChooseLanguageScreen
      type={ModalType.bottomSheet}
      open
      onClose={onBack}
      disableAnimation
    >
      <ModalContent>
        <Header>
          <HeaderTitle>{t('login.languageLabel')}</HeaderTitle>
        </Header>
        <ListContainer>
          <StyledSelectableList
            elements={languageElements}
            searchString={searchString}
            selectIconType={SelectIconType.radio}
            onChange={onSelectLanguage}
            onSearch={onSearchLanguage}
          />
        </ListContainer>

        <ButtonsContainer>
          <CancelButton
            fluid
            size={ButtonSize.big}
            type={ButtonType.transparent}
            onClick={handleCancel}
            border={themeManager.isLight() ? '1px solid var(--border-primary)' : '1px solid rgba(255, 255, 255, 0.2)'}
            textColor={themeManager.isLight() ? 'var(--text-primary)' : 'var(--r-color-white)'}
          >
            {t('common.cancel')}
          </CancelButton>

          <ConfirmButton
            fluid
            size={ButtonSize.big}
            type={ButtonType.primary}
            onClick={handleConfirm}
          >
            {t('common.confirm')}
          </ConfirmButton>
        </ButtonsContainer>
      </ModalContent>
    </StyledChooseLanguageScreen>
  )
}

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderTitle = styled.h2`
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  text-align: left;
  padding: 30px 20px 12px 20px;
`

const StyledChooseLanguageScreen = styled(Modal)`
  && {
    z-index: 10002 !important;

    .type-bottom-sheet {
      background: #1a1a1a;
      border-radius: 24px 24px 0 0;
      padding: 0px !important;
      overflow: hidden;

      [data-theme="light"] & {
        background: var(--modal-background);
      }
    }
  }

  .title {
    font-size: var(--font-size-extra-big);
    color: #FFFFFF;

    [data-theme="light"] & {
      color: var(--text-primary);
    }
  }
  .country-flag {
    border-radius: 50%;
  }
`

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  height: 51vh;

  [data-theme="light"] & {
    background: var(--modal-background);
  }
`

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(138, 90, 255, 0.6);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(138, 90, 255, 0.8);
  }
`

const StyledSelectableList = styled(ListWithSearch)`
  flex-grow: 1;
  background: #1a1a1a;

  .country-flag {
    border-radius: 50%;
  }

  [data-theme="light"] & {
    background: var(--modal-background);
  }
`

const ButtonsContainer = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px 20px 24px 20px;
  background: #1a1a1a;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  [data-theme="light"] & {
    background: var(--modal-background);
    border-top: 1px solid var(--border-primary);
  }
`

const CancelButton = styled(Button)`
  border-radius: 12px;
  font-weight: 500;
  font-size: 16px;
`

const ConfirmButton = styled(Button)`
  border-radius: 12px;
  font-weight: 500;
  font-size: 16px;
  background: var(--gradient-onboarding);
`

export default LoginChooseLanguageScreen
